<template>
  <div class="container">
    <!-- Success and Error Messages -->
    <div v-if="successMessage" class="alert alert-success alert-dismissible fade show fixed-alert" role="alert">
      {{ successMessage }}
      <button type="button" class="btn-close" @click="successMessage = ''"></button>
    </div>
    <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show fixed-alert" role="alert">
      {{ errorMessage }}
      <button type="button" class="btn-close" @click="errorMessage = ''"></button>
    </div>

    <!-- Page Title -->
    <div class="mt-4">
      <h2>{{ $t('absenceTitle') }}</h2>
    </div>

    <!-- Add Absence Type Button -->
    <div class="mb-4">
      <button class="btn btn-primary" @click="openModal('add')">{{ $t('addAbsence') }}
        <i class="bi bi-plus-circle"></i>
      </button>
    </div>

    <!-- Absence Type List Table -->
    <div v-if="absenceTypes.length > 0">
      <table class="table table-hover">
        <thead>
        <tr>
          <th>{{ $t('ID') }}</th>
          <th>{{ $t('name') }}</th>
          <th>{{ $t('absenceCategory') }}</th>
          <th>{{ $t('defaultQuota') }}</th>
          <th>{{ $t('color') }}</th>
          <th>{{ $t('emailNotification') }}</th>
          <th>{{ $t('actions') }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="absenceType in absenceTypes" :key="absenceType.id">
          <td>{{ absenceType.id }}</td>
          <td>{{ absenceType.name }}</td>
          <td>{{ categoryLabels[absenceType.category] || absenceType.category }}</td>
          <td>{{ absenceType.default_quota }} days</td>
          <td>
            <span :style="{ backgroundColor: absenceType.color_code }" class="color-box"></span> {{
              absenceType.color_code
            }}
          </td>
          <td>{{ absenceType.send_email_notification ?  $t('yes') : $t('no')  }}</td>
          <td>
            <button class="btn btn-primary btn-sm" @click="openModal('edit', absenceType)">
              <i class="bi bi-pencil-square"></i> {{ $t('edit') }}
            </button>
            <button class="btn btn-danger btn-sm" @click="openConfirmDelete(absenceType.id)">
              <i class="bi bi-trash"></i> {{ $t('delete') }}
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="alert alert-warning" role="alert">
      {{ $t('noAbsenceTypes') }}
    </div>

    <!-- Modal for Add/Edit Absence Type -->
    <div class="modal fade" id="absenceTypeModal" tabindex="-1" aria-labelledby="absenceTypeModalLabel"
         aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="absenceTypeModalLabel">{{
                isEditMode ? $t('editAbsenceType') : $t('addAbsenceType')
              }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitAbsenceType">
              <div class="mb-3">
                <label for="nameInput" class="form-label">{{ $t('name') }}</label>
                <input v-model="absenceTypeForm.name" type="text" class="form-control" id="nameInput" required/>
              </div>
              <div class="mb-3">
                <label for="categoryInput" class="form-label">{{ $t('absenceCategory') }}</label>
                <select class="form-select" id="categoryInput" v-model="absenceTypeForm.category">
                  <option v-for="category in categories" :key="category.value" :value="category.value">
                    {{ category.label }}
                  </option>
                </select>
              </div>
              <div class="mb-3">
                <label for="defaultQuotaInput" class="form-label">{{ $t('defaultQuota') }}</label>
                <input v-model="absenceTypeForm.default_quota" type="number" class="form-control" id="defaultQuotaInput"
                       required/>
              </div>
              <div class="mb-3">
                <label for="colorCodeInput" class="form-label">{{ $t('color') }}</label>
                <input v-model="absenceTypeForm.color_code" type="color" class="form-control custom-color-picker"
                       id="colorCodeInput"/>
              </div>
              <div class="form-check mb-3">
                <input v-model="absenceTypeForm.send_email_notification" type="checkbox" class="form-check-input"
                       id="emailNotificationInput"/>
                <label for="emailNotificationInput" class="form-check-label ms-2">{{ $t('sendEmail') }}</label>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('cancel') }}</button>
            <button type="button" class="btn btn-primary" @click="submitAbsenceType">{{
                isEditMode ? $t('update') : $t('add')
              }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Delete Modal -->
    <div v-if="showConfirm" class="modal-overlay">
      <div class="modal-container">
        <h3 class="modal-title">{{ $t('confirmDeleteTitle') }}</h3>
        <p>{{ $t('confirmDeleteAbsence') }}</p>
        <div class="modal-actions">
          <button class="btn btn-danger" @click="deleteAbsenceType">{{ $t('confirmDelete') }}</button>
          <button class="btn btn-primary" @click="cancelDelete">{{ $t('cancel') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AbsenceTypeList',
  data() {
    return {
      absenceTypes: [],
      successMessage: '',
      errorMessage: '',
      isEditMode: false,
      categoryLabels: {},
      categories: [],
      absenceTypeForm: {
        id: null,
        name: '',
        category: '',
        default_quota: 0,
        color_code: '#33b1db',
        send_email_notification: false
      },
      modalInstance: null,
      showConfirm: false,
      absenceTypeIdToDelete: null,
    };
  },
  mounted() {
    this.getAbsenceTypes();
    this.fetchCategories();
    this.$nextTick(() => {
      const modalElement = document.getElementById('absenceTypeModal');
      if (modalElement) {
        this.modalInstance = new bootstrap.Modal(modalElement);
      }
    });
  },
  methods: {
    openModal(mode, absenceType = null) {
      if (mode === 'add') {
        this.isEditMode = false;
        this.absenceTypeForm = {
          id: null,
          name: '',
          category: '',
          default_quota: 0,
          color_code: '#33b1db',
          send_email_notification: false
        };
      } else if (mode === 'edit') {
        this.isEditMode = true;
        this.absenceTypeForm = {...absenceType};
      }
      this.modalInstance.show();
    },
    closeModal() {
      this.modalInstance.hide();
    },
    async submitAbsenceType() {
      try {
        if (this.isEditMode) {
          await axios.put(`/api/v1/vacation/absence-types/${this.absenceTypeForm.id}/`, this.absenceTypeForm);
          this.successMessage = 'Absence type updated successfully';
        } else {
          await axios.post(`/api/v1/vacation/absence-types/`, this.absenceTypeForm);
          this.successMessage = 'Absence type added successfully';
        }
        this.getAbsenceTypes();
      } catch (error) {
        this.errorMessage = 'Failed to save absence type';
      } finally {
        this.closeModal();
        this.resetForm();
        setTimeout(() => {
          this.successMessage = '';
          this.errorMessage = '';
        }, 3000);
      }
    },
    openConfirmDelete(absenceTypeId) {
      this.absenceTypeIdToDelete = absenceTypeId;
      this.showConfirm = true;
    },
    async deleteAbsenceType() {
      try {
        await axios.delete(`/api/v1/vacation/absence-types/${this.absenceTypeIdToDelete}/`);
        this.successMessage = 'Absence type deleted successfully';
        this.getAbsenceTypes();
      } catch (error) {
        this.errorMessage = 'Failed to delete absence type';
      } finally {
        this.showConfirm = false;
        setTimeout(() => {
          this.successMessage = '';
          this.errorMessage = '';
        }, 3000);
      }
    },
    cancelDelete() {
      this.showConfirm = false;
    },
    async getAbsenceTypes() {
      try {
        const response = await axios.get('/api/v1/vacation/absence-types/');
        this.absenceTypes = response.data.results;
      } catch (error) {
        this.errorMessage = 'Failed to fetch absence types';
        setTimeout(() => {
          this.errorMessage = '';
        }, 3000);
      }
    },
    fetchCategories() {
      axios.get('/api/v1/vacation/absence-types/categories/')
          .then(response => {
            this.categories = response.data;
            // Convert list to a mapping object { "VACATION": "Paid Vacation", ... }
            this.categoryLabels = response.data.reduce((acc, category) => {
              acc[category.value] = category.label;
              return acc;

            }, {});
          })
          .catch(error => {
            console.error("Error fetching categories:", error);
          });
    },
    resetForm() {
      this.absenceTypeForm = {
        id: null,
        name: '',
        default_quota: 0,
        color_code: '#33b1db',
        send_email_notification: false
      };
    }
  },
};
</script>

<style scoped>
.color-box {
  display: inline-block;
  width: 15px;
  height: 15px;
  border-radius: 3px;
  margin-right: 5px;
}

.custom-color-picker {
  width: 100%; /* Full width */
  height: 20px; /* Double the default height */
  border-radius: 12px; /* Rounded corners */
  padding: 0;
  cursor: pointer; /* Pointer cursor to indicate clickable area */
  border: 1px solid #33b1db; /* Border styling */
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 300px;
}
</style>
